import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import ImportorSelect from "../components/ImportorSelect";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STORES = ["Sport 1 Røyken", "Sport 1 Slemmestad", "Røyken Sykkelverksted"];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function rowKey(e, i) {
  return e._id ? String(e._id) : `svc-${e.serviceId}-${e.partIndex}-${i}`;
}

function emptyForm(butikk = "") {
  return { butikk, product: "", brand: "", ean: "", price: "", qty: "1", kjopsdato: "", rammenr: "", tidsforbruk: "", arsak: "", ansvar: "", importor: "", status: "Åpen" };
}

function entryToForm(e) {
  return {
    butikk: e.butikk || "",
    product: e.product || "",
    brand: e.brand || "",
    ean: e.ean || "",
    price: e.price != null ? String(e.price) : "",
    qty: e.qty != null ? String(e.qty) : "1",
    kjopsdato: e.kjopsdato || "",
    rammenr: e.rammenr || "",
    tidsforbruk: e.tidsforbruk != null ? String(e.tidsforbruk) : "",
    arsak: e.arsak || "",
    ansvar: e.ansvar || "",
    importor: e.importor || "",
    merke: e.merke || "",
    modell: e.modell || "",
    serienr: e.serienr || "",
    status: e.status || "Åpen",
  };
}

function StatusBadge({ status }) {
  return status === "Avsluttet"
    ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Avsluttet</span>
    : <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">Åpen</span>;
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ url, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <img
        src={url}
        alt="Reklamasjon"
        className="max-h-full max-w-full rounded shadow-xl object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full w-8 h-8 flex items-center justify-center text-lg"
      >
        ×
      </button>
    </div>
  );
}

// ── ImageSection ──────────────────────────────────────────────────────────────
function ImageSection({ entry, onEntryUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const fileRef = useRef();

  const images = entry.images || [];

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);

      let r;
      if (entry.source === "direkte") {
        r = await fetch(`${API_BASE_URL}/reklamasjon/${entry._id}/images`, { method: "POST", body: fd });
      } else {
        r = await fetch(`${API_BASE_URL}/workshop-services/${entry.serviceId}/parts/${entry.partIndex}/reklamasjon-images`, { method: "POST", body: fd });
      }

      if (!r.ok) throw new Error();
      const updated = await r.json();
      onEntryUpdated(
        entry.source === "direkte"
          ? { ...updated }
          : { ...entry, images: updated.images }
      );
    } catch {
      alert("Bildeopplasting feilet.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDeleteImage(imgIdx) {
    if (!window.confirm("Slett dette bildet?")) return;
    try {
      let r;
      if (entry.source === "direkte") {
        r = await fetch(`${API_BASE_URL}/reklamasjon/${entry._id}/images/${imgIdx}`, { method: "DELETE" });
      } else {
        r = await fetch(`${API_BASE_URL}/workshop-services/${entry.serviceId}/parts/${entry.partIndex}/reklamasjon-images/${imgIdx}`, { method: "DELETE" });
      }
      if (!r.ok) throw new Error();
      const updated = await r.json();
      onEntryUpdated(
        entry.source === "direkte"
          ? { ...updated }
          : { ...entry, images: updated.images }
      );
    } catch {
      alert("Kunne ikke slette bilde.");
    }
  }

  return (
    <div className="mt-5 border-t pt-4">
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Bilder ({images.length})</span>
        <label className={`cursor-pointer text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? "Laster opp…" : "+ Legg til bilde"}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      {images.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {images.map((img, i) => (
            <div key={i} className="relative group w-20 h-20">
              <img
                src={img.url}
                alt={`Bilde ${i + 1}`}
                className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-90"
                onClick={() => setLightboxUrl(img.url)}
              />
              <button
                onClick={() => handleDeleteImage(i)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-600 text-white text-xs leading-none items-center justify-center hidden group-hover:flex"
                title="Slett bilde"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400">Ingen bilder lagt til.</p>
      )}
    </div>
  );
}

// ── EditModal ─────────────────────────────────────────────────────────────────
function EditModal({ entry, onSave, onClose }) {
  const [form, setForm] = useState(entryToForm(entry));
  const [saving, setSaving] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(entry);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.product.trim()) { alert("Produktnavn er påkrevd."); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        price: Number(form.price) || 0,
        qty: Math.max(1, Number(form.qty) || 1),
        tidsforbruk: form.tidsforbruk !== "" ? Number(form.tidsforbruk) : null,
      };

      let updated;
      if (currentEntry.source === "direkte") {
        const r = await fetch(`${API_BASE_URL}/reklamasjon/${currentEntry._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error();
        updated = await r.json();
      } else {
        // For service parts, patch the workshop service reklamasjon fields
        const reklamasjon = {
          butikk: form.butikk,
          kjopsdato: form.kjopsdato,
          rammenr: form.rammenr,
          tidsforbruk: form.tidsforbruk !== "" ? Number(form.tidsforbruk) : null,
          arsak: form.arsak,
          ansvar: form.ansvar,
          importor: form.importor,
          merke: form.merke,
          modell: form.modell,
          serienr: form.serienr,
          status: form.status,
          images: currentEntry.images || [],
        };
        const r = await fetch(`${API_BASE_URL}/workshop-services/${currentEntry.serviceId}/parts/${currentEntry.partIndex}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ean: form.ean, brand: form.brand, product: form.product, price: Number(form.price) || 0, qty: Math.max(1, Number(form.qty) || 1), reklamasjon }),
        });
        if (!r.ok) throw new Error();
        // Rebuild the combined entry shape
        updated = { ...currentEntry, ...form, price: Number(form.price) || 0, qty: Math.max(1, Number(form.qty) || 1), tidsforbruk: form.tidsforbruk !== "" ? Number(form.tidsforbruk) : null };
      }
      onSave(updated);
    } catch {
      alert("Feil ved lagring.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-xl overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-lg">Endre reklamasjon</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSave} className="p-5">
          {currentEntry.source === "service" && (
            <div className="mb-4 text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded px-3 py-2">
              Denne reklamasjonen er knyttet til en service. Delinfo og reklamasjonsfelter lagres tilbake på servicen.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Produktnavn *</label>
              <input className="w-full border rounded p-2 text-sm" value={form.product} onChange={(e) => setField("product", e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Varemerke</label>
              <input className="w-full border rounded p-2 text-sm" value={form.brand} onChange={(e) => setField("brand", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">EAN</label>
              <input className="w-full border rounded p-2 text-sm" value={form.ean} onChange={(e) => setField("ean", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pris (kr)</label>
              <input type="number" className="w-full border rounded p-2 text-sm" min="0" value={form.price} onChange={(e) => setField("price", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Antall</label>
              <input type="number" className="w-full border rounded p-2 text-sm" min="1" value={form.qty} onChange={(e) => setField("qty", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Butikk</label>
              <select className="w-full border rounded p-2 text-sm" value={form.butikk} onChange={(e) => setField("butikk", e.target.value)}>
                <option value="">Velg butikk…</option>
                {STORES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Kundens kjøpsdato</label>
              <input type="date" className="w-full border rounded p-2 text-sm" value={form.kjopsdato} onChange={(e) => setField("kjopsdato", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Rammenr. sykkel</label>
              <input className="w-full border rounded p-2 text-sm" placeholder="Rammenummer" value={form.rammenr} onChange={(e) => setField("rammenr", e.target.value)} />
            </div>
            {currentEntry.source === "service" && (
              <>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Serienr. del</label>
                  <input className="w-full border rounded p-2 text-sm" placeholder="Serienummer på delen" value={form.serienr} onChange={(e) => setField("serienr", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Merke</label>
                  <input className="w-full border rounded p-2 text-sm" placeholder="Merke" value={form.merke} onChange={(e) => setField("merke", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Modell</label>
                  <input className="w-full border rounded p-2 text-sm" placeholder="Modell" value={form.modell} onChange={(e) => setField("modell", e.target.value)} />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Tidsforbruk (min)</label>
              <input type="number" className="w-full border rounded p-2 text-sm" min="0" value={form.tidsforbruk} onChange={(e) => setField("tidsforbruk", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Ansvar</label>
              <select className="w-full border rounded p-2 text-sm" value={form.ansvar} onChange={(e) => setField("ansvar", e.target.value)}>
                <option value="">Velg…</option>
                <option value="Intern">Intern</option>
                <option value="Ekstern">Ekstern</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Importør</label>
              <ImportorSelect value={form.importor} onChange={(v) => setField("importor", v)} />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Status</label>
              <select className="w-full border rounded p-2 text-sm" value={form.status} onChange={(e) => setField("status", e.target.value)}>
                <option value="Åpen">Åpen</option>
                <option value="Avsluttet">Avsluttet</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Reklamasjonsårsak</label>
              <textarea className="w-full border rounded p-2 text-sm" rows={3} value={form.arsak} onChange={(e) => setField("arsak", e.target.value)} />
            </div>
          </div>

          {/* Bilder */}
          <ImageSection
            entry={currentEntry}
            onEntryUpdated={(updated) => setCurrentEntry(updated)}
          />

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm">Avbryt</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 text-sm">
              {saving ? "Lagrer…" : "Lagre endringer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReklamasjonSykkel() {
  const navigate = useNavigate();
  const activeStore = Cookies.get("selectedStore") || "";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm(activeStore));
  const [filterButikk, setFilterButikk] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/reklamasjon`);
      const data = r.ok ? await r.json() : [];
      setEntries(data);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.product.trim()) { setMsg("Produktnavn er påkrevd."); return; }
    setSaving(true);
    setMsg("");
    try {
      const body = { ...form, price: Number(form.price) || 0, qty: Math.max(1, Number(form.qty) || 1), tidsforbruk: form.tidsforbruk !== "" ? Number(form.tidsforbruk) : null };
      const r = await fetch(`${API_BASE_URL}/reklamasjon`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      const created = await r.json();
      setEntries((prev) => [created, ...prev]);
      setForm(emptyForm(activeStore));
      setShowForm(false);
      setMsg("Reklamasjon registrert.");
    } catch {
      setMsg("Feil ved lagring.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Slett denne reklamasjonen?")) return;
    try {
      const r = await fetch(`${API_BASE_URL}/reklamasjon/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      setEntries((prev) => prev.filter((e) => e._id !== id));
    } catch {
      setMsg("Feil ved sletting.");
    }
  }

  function handleEditSave(updated) {
    setEntries((prev) => prev.map((e) => {
      if (updated.source === "direkte" && e._id === updated._id) return updated;
      if (updated.source === "service" && e.source === "service" && String(e.serviceId) === String(updated.serviceId) && e.partIndex === updated.partIndex) return updated;
      return e;
    }));
    setEditingEntry(null);
  }

  const filtered = filterButikk ? entries.filter((e) => e.butikk === filterButikk) : entries;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {editingEntry && (
        <EditModal
          entry={editingEntry}
          onSave={handleEditSave}
          onClose={() => setEditingEntry(null)}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold">Reklamasjon sykkel</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => navigate(-1)} className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm">Tilbake</button>
          <button onClick={() => { setShowForm((v) => !v); setMsg(""); }} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm">
            {showForm ? "Skjul skjema" : "+ Ny reklamasjon"}
          </button>
        </div>
      </div>

      {msg && (
        <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{msg}</div>
      )}

      {/* Registreringsskjema */}
      {showForm && (
        <div className="border rounded-lg p-4 mb-6 bg-gray-50">
          <h3 className="font-semibold mb-4">Registrer ny reklamasjon</h3>
          <form onSubmit={handleSave}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Produktnavn *</label>
                <input className="w-full border rounded p-2 text-sm" placeholder="Navn på del" value={form.product} onChange={(e) => setField("product", e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Varemerke</label>
                <input className="w-full border rounded p-2 text-sm" value={form.brand} onChange={(e) => setField("brand", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">EAN</label>
                <input className="w-full border rounded p-2 text-sm" value={form.ean} onChange={(e) => setField("ean", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Pris (kr)</label>
                <input type="number" className="w-full border rounded p-2 text-sm" min="0" value={form.price} onChange={(e) => setField("price", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Antall</label>
                <input type="number" className="w-full border rounded p-2 text-sm" min="1" value={form.qty} onChange={(e) => setField("qty", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Butikk</label>
                <select className="w-full border rounded p-2 text-sm" value={form.butikk} onChange={(e) => setField("butikk", e.target.value)}>
                  <option value="">Velg butikk…</option>
                  {STORES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Kundens kjøpsdato</label>
                <input type="date" className="w-full border rounded p-2 text-sm" value={form.kjopsdato} onChange={(e) => setField("kjopsdato", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Rammenr. sykkel</label>
                <input className="w-full border rounded p-2 text-sm" placeholder="Rammenummer" value={form.rammenr} onChange={(e) => setField("rammenr", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Tidsforbruk (min)</label>
                <input type="number" className="w-full border rounded p-2 text-sm" min="0" value={form.tidsforbruk} onChange={(e) => setField("tidsforbruk", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Ansvar</label>
                <select className="w-full border rounded p-2 text-sm" value={form.ansvar} onChange={(e) => setField("ansvar", e.target.value)}>
                  <option value="">Velg…</option>
                  <option value="Intern">Intern</option>
                  <option value="Ekstern">Ekstern</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Importør</label>
                <ImportorSelect value={form.importor} onChange={(v) => setField("importor", v)} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Status</label>
                <select className="w-full border rounded p-2 text-sm" value={form.status} onChange={(e) => setField("status", e.target.value)}>
                  <option value="Åpen">Åpen</option>
                  <option value="Avsluttet">Avsluttet</option>
                </select>
              </div>
              <div className="md:col-span-4">
                <label className="block text-xs text-gray-600 mb-1">Reklamasjonsårsak</label>
                <textarea className="w-full border rounded p-2 text-sm" rows={2} placeholder="Beskriv årsaken til reklamasjon…" value={form.arsak} onChange={(e) => setField("arsak", e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="px-3 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm" onClick={() => { setShowForm(false); setForm(emptyForm(activeStore)); setMsg(""); }}>Avbryt</button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 text-sm">{saving ? "Lagrer…" : "Lagre reklamasjon"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Filterrad */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm text-gray-600">Butikk:</label>
        <select className="border rounded p-1.5 text-sm" value={filterButikk} onChange={(e) => setFilterButikk(e.target.value)}>
          <option value="">Alle butikker</option>
          {STORES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-sm text-gray-500 ml-auto">{filtered.length} poster</span>
      </div>

      {/* Tabell */}
      {loading ? (
        <div className="py-12 text-center text-gray-500">Henter reklamasjoner…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-gray-400">Ingen reklamasjoner registrert.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left text-xs text-gray-600 uppercase tracking-wide">
                <th className="px-3 py-2 border-b whitespace-nowrap">Reg. dato</th>
                <th className="px-3 py-2 border-b whitespace-nowrap">Kilde</th>
                <th className="px-3 py-2 border-b">Butikk</th>
                <th className="px-3 py-2 border-b">Varemerke</th>
                <th className="px-3 py-2 border-b">Produkt</th>
                <th className="px-3 py-2 border-b">Importør</th>
                <th className="px-3 py-2 border-b">Ansvar</th>
                <th className="px-3 py-2 border-b">Reklamasjonsårsak</th>
                <th className="px-3 py-2 border-b whitespace-nowrap">Status</th>
                <th className="px-3 py-2 border-b"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((e, i) => (
                <tr key={rowKey(e, i)} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmtDate(e.createdAt)}</td>
                  <td className="px-3 py-2">
                    {e.source === "service"
                      ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Service</span>
                      : <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Direkte</span>
                    }
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{e.butikk || "—"}</td>
                  <td className="px-3 py-2">{e.brand || "—"}</td>
                  <td className="px-3 py-2 font-medium">{e.product || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{e.importor || "—"}</td>
                  <td className="px-3 py-2">
                    {e.ansvar ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.ansvar === "Ekstern" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                        {e.ansvar}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 max-w-xs text-gray-700">{e.arsak || "—"}</td>
                  <td className="px-3 py-2"><StatusBadge status={e.status || "Åpen"} /></td>
                  <td className="px-3 py-2">
                    {e.sperret ? (
                      <span
                        className="text-xs text-gray-600 whitespace-nowrap"
                        title="Kunden er slettet. Servicen er sperret og kan ikke endres, men reklamasjonen vises fordi kravet mot leverandøren kan være åpent."
                      >
                        Låst (kunde slettet)
                      </span>
                    ) : (
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setEditingEntry(e)}
                        className="text-xs px-2 py-0.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 whitespace-nowrap"
                      >
                        Endre
                      </button>
                      {e.source === "direkte" ? (
                        <button
                          onClick={() => handleDelete(e._id)}
                          className="text-xs px-2 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50 whitespace-nowrap"
                        >
                          Slett
                        </button>
                      ) : (
                        <a
                          href={`/workshop/${e.serviceId}`}
                          className="text-xs px-2 py-0.5 rounded border border-purple-200 text-purple-600 hover:bg-purple-50 whitespace-nowrap"
                        >
                          Åpne service
                        </a>
                      )}
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
